/**
 * Nesting Validator
 *
 * Validates that container nesting operations comply with nesting constraints.
 * Primary rules:
 * - Maximum 2-level nesting (root → nested, no deeper)
 * - No circular nesting (A cannot be nested inside A's descendant)
 */

import type { ContainerAPI } from '../../types/api.types';
import type { ValidationResult, ValidationError, ValidationWarning } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS, type ContainerConstraints } from '../constants/constraints';
import {
  getNestingDepth,
  isAncestorOf,
  findContainerById,
} from '../utils/containerTraversal';

/**
 * Validates nesting depth when adding a nested container
 *
 * @param containers - Top-level containers in the canvas
 * @param parentContainerId - ID of the parent container (null for top-level)
 * @param constraints - Container constraints (optional)
 * @returns Validation result
 */
export const validateNestingDepth = (
  containers: ContainerAPI[],
  parentContainerId: string | null,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const errors: ValidationError[] = [];

  // If no parent, it's a top-level container - always valid
  if (!parentContainerId) {
    return { isValid: true, errors: [], warnings: [] };
  }

  // Find the parent container
  const parentContainer = findContainerById(containers, parentContainerId);
  if (!parentContainer) {
    errors.push({
      code: 'CONTAINER_NOT_FOUND',
      message: `Parent container "${parentContainerId}" not found.`,
      context: { parentContainerId },
    });
    return { isValid: false, errors, warnings: [] };
  }

  // Get parent's nesting depth
  const parentDepth = getNestingDepth(containers, parentContainerId);

  // New container will be at parentDepth + 1
  const newDepth = parentDepth + 1;

  if (newDepth >= constraints.MAX_NESTING_DEPTH) {
    errors.push({
      code: 'NESTING_TOO_DEEP',
      message: `Cannot nest container. Maximum nesting depth is ${constraints.MAX_NESTING_DEPTH} levels (current parent is at depth ${parentDepth}).`,
      context: {
        parentContainerId,
        parentDepth,
        wouldBeDepth: newDepth,
        maxAllowed: constraints.MAX_NESTING_DEPTH,
      },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [],
  };
};

/**
 * Validates that nesting doesn't create a circular reference
 *
 * @param containers - Top-level containers in the canvas
 * @param movingContainerId - ID of the container being moved/nested
 * @param targetParentId - ID of the target parent container
 * @returns Validation result
 */
export const validateNoCircularNesting = (
  containers: ContainerAPI[],
  movingContainerId: string,
  targetParentId: string
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Cannot nest a container inside itself
  if (movingContainerId === targetParentId) {
    errors.push({
      code: 'CIRCULAR_NESTING',
      message: 'Cannot nest a container inside itself.',
      context: { containerId: movingContainerId },
    });
    return { isValid: false, errors, warnings: [] };
  }

  // Cannot nest a container inside one of its descendants
  if (isAncestorOf(containers, movingContainerId, targetParentId)) {
    errors.push({
      code: 'CIRCULAR_NESTING',
      message: 'Cannot nest a container inside one of its descendants.',
      context: {
        movingContainerId,
        targetParentId,
        relationship: 'ancestor-descendant',
      },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [],
  };
};

/**
 * Validates whether a container can accept nested containers
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - ID of the container to check
 * @param constraints - Container constraints (optional)
 * @returns Validation result
 */
export const validateCanNest = (
  containers: ContainerAPI[],
  containerId: string,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const errors: ValidationError[] = [];

  const depth = getNestingDepth(containers, containerId);

  if (depth < 0) {
    errors.push({
      code: 'CONTAINER_NOT_FOUND',
      message: `Container "${containerId}" not found.`,
      context: { containerId },
    });
    return { isValid: false, errors, warnings: [] };
  }

  // Container at depth (MAX_NESTING_DEPTH - 1) cannot accept nested containers
  if (depth >= constraints.MAX_NESTING_DEPTH - 1) {
    errors.push({
      code: 'NESTING_TOO_DEEP',
      message: `Container at depth ${depth} cannot accept nested containers. Maximum nesting depth is ${constraints.MAX_NESTING_DEPTH} levels.`,
      context: {
        containerId,
        currentDepth: depth,
        maxAllowed: constraints.MAX_NESTING_DEPTH,
      },
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [],
  };
};

/**
 * Full nesting validation for a move operation
 *
 * @param containers - Top-level containers in the canvas
 * @param movingContainerId - ID of the container being moved
 * @param targetParentId - ID of the target parent (null for top-level)
 * @param constraints - Container constraints (optional)
 * @returns Validation result
 */
export const validateNestingOperation = (
  containers: ContainerAPI[],
  movingContainerId: string,
  targetParentId: string | null,
  constraints: ContainerConstraints = CONTAINER_CONSTRAINTS
): ValidationResult => {
  const allErrors: ValidationError[] = [];
  const allWarnings: ValidationWarning[] = [];

  // Validate depth constraint
  const depthResult = validateNestingDepth(containers, targetParentId, constraints);
  allErrors.push(...depthResult.errors);

  // If there's a parent, validate circular reference
  if (targetParentId) {
    const circularResult = validateNoCircularNesting(
      containers,
      movingContainerId,
      targetParentId
    );
    allErrors.push(...circularResult.errors);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};
